import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { api, formatError } from "../lib/api";
import { Text, Area, Btn, ImageUpload, StringList, Card, Field } from "./ui";

const CATS = ["EVENT CORE", "EXPERIENCE CORE", "TECH CORE"];
const TABS = ["Branding", "Hero", "About", "Services", "Stats", "Manifesto", "Contact", "Products", "Team"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { site: ctxSite, products: ctxProducts, team: ctxTeam, reload } = useContent();
  const navigate = useNavigate();

  const [tab, setTab] = useState("Branding");
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(ctxSite)));
  const [products, setProducts] = useState(() => JSON.parse(JSON.stringify(ctxProducts)));
  const [team, setTeam] = useState(() => JSON.parse(JSON.stringify(ctxTeam)));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const setHero = (k, v) => setSite((s) => ({ ...s, hero: { ...s.hero, [k]: v } }));
  const setAbout = (k, v) => setSite((s) => ({ ...s, about: { ...s.about, [k]: v } }));
  const setContact = (k, v) => setSite((s) => ({ ...s, contact: { ...s.contact, [k]: v } }));
  const setLogo = (k, v) => setSite((s) => ({ ...s, logos: { ...s.logos, [k]: v } }));
  const setSocial = (k, v) => setSite((s) => ({ ...s, social: { ...(s.social || {}), [k]: v } }));

  const saveSite = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", site);
      await reload();
      toast.success("Content saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  // ---- Products ----
  const addProduct = async () => {
    try {
      const { data } = await api.post("/admin/products", { name: "New product", category: "Event Technology", desc: "", image: "" });
      setProducts((p) => [...p, data]);
      toast.success("Product added");
    } catch (e) { toast.error(formatError(e)); }
  };
  const saveProduct = async (p) => {
    try { await api.put(`/admin/products/${p.id}`, p); await reload(); toast.success("Product saved"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const delProduct = async (id) => {
    try { await api.delete(`/admin/products/${id}`); setProducts((p) => p.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const updP = (id, k, v) => setProducts((list) => list.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  // ---- Team ----
  const addMember = async () => {
    try { const { data } = await api.post("/admin/team", { name: "New member", role: "", bio: "", photo: "" }); setTeam((t) => [...t, data]); toast.success("Member added"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const saveMember = async (m) => {
    try { await api.put(`/admin/team/${m.id}`, m); await reload(); toast.success("Member saved"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const delMember = async (id) => {
    try { await api.delete(`/admin/team/${id}`); setTeam((t) => t.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const updM = (id, k, v) => setTeam((list) => list.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  // ---- object-array helpers for site ----
  const updArr = (key, i, patch) => set(key, site[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addArr = (key, blank) => set(key, [...(site[key] || []), blank]);
  const delArr = (key, i) => set(key, site[key].filter((_, idx) => idx !== i));

  const siteTab = tab !== "Products" && tab !== "Team";

  return (
    <div className="grain min-h-screen bg-korio-bg text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-korio-bg/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img src="/brand/name_white.webp" alt="KORIO" className="h-6" />
            <span className="text-xs uppercase tracking-[0.25em] text-white/40">Back-office</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white">
              View site <ExternalLink size={14} />
            </a>
            <span className="hidden text-sm text-white/40 md:inline">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-8 px-6 py-8">
        {/* Sidebar */}
        <aside className="w-48 flex-shrink-0">
          <nav className="sticky top-24 space-y-1">
            {TABS.map((t) => (
              <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                className={`block w-full rounded-lg px-4 py-2.5 text-left text-sm font-medium transition-colors ${tab === t ? "brand-gradient text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="min-w-0 flex-1">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="font-display text-3xl font-medium tracking-tight">{tab}</h1>
            {siteTab && (
              <Btn onClick={saveSite} disabled={saving} testid="save-site">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
              </Btn>
            )}
          </div>

          {tab === "Branding" && (
            <Card className="space-y-5">
              <Text label="Slogan" value={site.slogan} onChange={(v) => set("slogan", v)} testid="f-slogan" />
              <div className="grid gap-5 sm:grid-cols-2">
                <ImageUpload label="Logo (gradient)" value={site.logos?.name} onChange={(v) => setLogo("name", v)} />
                <ImageUpload label="Logo (white)" value={site.logos?.name_white} onChange={(v) => setLogo("name_white", v)} />
                <ImageUpload label="Icon K (gradient)" value={site.logos?.k} onChange={(v) => setLogo("k", v)} />
                <ImageUpload label="Icon K (white)" value={site.logos?.k_white} onChange={(v) => setLogo("k_white", v)} />
              </div>
            </Card>
          )}

          {tab === "Hero" && (
            <Card className="space-y-5">
              <Text label="Overline" value={site.hero?.overline} onChange={(v) => setHero("overline", v)} />
              <StringList label="Headline lines" items={site.hero?.lines || []} onChange={(v) => setHero("lines", v)} placeholder="Line of the headline" />
              <Area label="Paragraph" value={site.hero?.paragraph} onChange={(v) => setHero("paragraph", v)} />
              <ImageUpload label="Hero background image" value={site.hero?.image} onChange={(v) => setHero("image", v)} />
            </Card>
          )}

          {tab === "About" && (
            <Card className="space-y-5">
              <Text label="Overline" value={site.about?.overline} onChange={(v) => setAbout("overline", v)} />
              <StringList label="Heading lines" items={site.about?.headingLines || []} onChange={(v) => setAbout("headingLines", v)} />
              <Area label="Intro" value={site.about?.intro} onChange={(v) => setAbout("intro", v)} rows={2} />
              <Area label="Lead sentence" value={site.about?.lead} onChange={(v) => setAbout("lead", v)} rows={2} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Text label="CORE — title" value={site.about?.core?.title} onChange={(v) => setAbout("core", { ...site.about.core, title: v })} />
                <Text label="I/O — title" value={site.about?.io?.title} onChange={(v) => setAbout("io", { ...site.about.io, title: v })} />
                <Area label="CORE — body" value={site.about?.core?.body} onChange={(v) => setAbout("core", { ...site.about.core, body: v })} rows={3} />
                <Area label="I/O — body" value={site.about?.io?.body} onChange={(v) => setAbout("io", { ...site.about.io, body: v })} rows={3} />
              </div>
              <Area label="Body paragraph" value={site.about?.body} onChange={(v) => setAbout("body", v)} />
              <Text label="Pull quote" value={site.about?.quote} onChange={(v) => setAbout("quote", v)} />
              <Text label="Team note" value={site.about?.teamNote} onChange={(v) => setAbout("teamNote", v)} />
            </Card>
          )}

          {tab === "Services" && (
            <div className="space-y-5">
              {(site.services || []).map((s, i) => (
                <Card key={i} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Text label="Title" value={s.title} onChange={(v) => updArr("services", i, { title: v })} />
                    <Text label="Index label" value={s.index} onChange={(v) => updArr("services", i, { index: v })} />
                    <Text label="Tagline" value={s.tagline} onChange={(v) => updArr("services", i, { tagline: v })} />
                    <Text label="Category" value={s.category} onChange={(v) => updArr("services", i, { category: v })} />
                  </div>
                  <Area label="Summary" value={s.summary} onChange={(v) => updArr("services", i, { summary: v })} rows={2} />
                  <ImageUpload label="Image" value={s.image} onChange={(v) => updArr("services", i, { image: v })} />
                  <StringList label="Bullet points" items={s.points || []} onChange={(v) => updArr("services", i, { points: v })} />
                </Card>
              ))}
            </div>
          )}

          {tab === "Stats" && (
            <Card className="space-y-4">
              {(site.stats || []).map((st, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] items-end gap-3">
                  <Text label="Value" value={String(st.value)} onChange={(v) => updArr("stats", i, { value: Number(v) || 0 })} />
                  <Text label="Suffix" value={st.suffix} onChange={(v) => updArr("stats", i, { suffix: v })} />
                  <Text label="Label" value={st.label} onChange={(v) => updArr("stats", i, { label: v })} />
                  <button onClick={() => delArr("stats", i)} className="mb-1 rounded-lg border border-white/10 px-3 py-2.5 text-white/50 hover:text-red-300"><Trash2 size={16} /></button>
                </div>
              ))}
              <button onClick={() => addArr("stats", { value: 0, suffix: "", label: "" })} className="inline-flex items-center gap-1.5 text-xs font-medium text-volt"><Plus size={14} /> Add stat</button>
            </Card>
          )}

          {tab === "Manifesto" && (
            <div className="space-y-5">
              {(site.manifesto || []).map((m, i) => (
                <Card key={i} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="w-24"><Text label="Number" value={m.n} onChange={(v) => updArr("manifesto", i, { n: v })} /></div>
                    <button onClick={() => delArr("manifesto", i)} className="rounded-lg border border-white/10 px-3 py-2 text-white/50 hover:text-red-300"><Trash2 size={16} /></button>
                  </div>
                  <Text label="Title" value={m.title} onChange={(v) => updArr("manifesto", i, { title: v })} />
                  <Area label="Body" value={m.body} onChange={(v) => updArr("manifesto", i, { body: v })} rows={2} />
                </Card>
              ))}
              <button onClick={() => addArr("manifesto", { n: "", title: "", body: "" })} className="inline-flex items-center gap-1.5 text-sm font-medium text-volt"><Plus size={14} /> Add chapter</button>
            </div>
          )}

          {tab === "Contact" && (
            <Card className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Text label="Email" value={site.contact?.email} onChange={(v) => setContact("email", v)} />
                <Text label="Phone" value={site.contact?.phone} onChange={(v) => setContact("phone", v)} />
                <Text label="Phone link (href)" value={site.contact?.phoneHref} onChange={(v) => setContact("phoneHref", v)} hint="e.g. tel:+3227023636" />
                <Text label="Company" value={site.contact?.company} onChange={(v) => setContact("company", v)} />
                <Text label="VAT" value={site.contact?.vat} onChange={(v) => setContact("vat", v)} />
              </div>
              <StringList label="Address lines" items={site.contact?.address || []} onChange={(v) => setContact("address", v)} />
              <Area label="Footer blurb" value={site.footerBlurb} onChange={(v) => set("footerBlurb", v)} rows={2} />
              <StringList label="Client names (marquee)" items={site.clients || []} onChange={(v) => set("clients", v)} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Text label="Instagram URL" value={site.social?.instagram} onChange={(v) => setSocial("instagram", v)} hint="https://www.instagram.com/..." />
                <Text label="LinkedIn URL" value={site.social?.linkedin} onChange={(v) => setSocial("linkedin", v)} hint="https://www.linkedin.com/company/..." />
              </div>
            </Card>
          )}

          {tab === "Products" && (
            <div className="space-y-5">
              <Btn onClick={addProduct} testid="add-product"><Plus size={16} /> Add product</Btn>
              {products.map((p) => (
                <Card key={p.id} className="space-y-4" >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Text label="Name" value={p.name} onChange={(v) => updP(p.id, "name", v)} />
                    <Field label="Category">
                      <select className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-volt"
                        value={p.category} onChange={(e) => updP(p.id, "category", e.target.value)}>
                        {CATS.map((c) => <option key={c} value={c} className="bg-korio-elevated">{c}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Area label="Description" value={p.desc} onChange={(v) => updP(p.id, "desc", v)} rows={2} />
                  <Text label="Slug (URL)" value={p.slug} onChange={(v) => updP(p.id, "slug", v)} hint="e.g. access-control" />
                  <Area label="Detailed description" value={p.long} onChange={(v) => updP(p.id, "long", v)} rows={5} hint="Leave a blank line between paragraphs" />
                  <StringList label="What's inside (features)" items={p.features || []} onChange={(v) => updP(p.id, "features", v)} />
                  <ImageUpload label="Image" value={p.image} onChange={(v) => updP(p.id, "image", v)} />
                  <div className="flex gap-3">
                    <Btn onClick={() => saveProduct(p)}><Save size={16} /> Save</Btn>
                    <Btn variant="danger" onClick={() => delProduct(p.id)}><Trash2 size={16} /> Delete</Btn>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {tab === "Team" && (
            <div className="space-y-5">
              <Btn onClick={addMember} testid="add-member"><Plus size={16} /> Add member</Btn>
              {team.map((m) => (
                <Card key={m.id} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Text label="Name" value={m.name} onChange={(v) => updM(m.id, "name", v)} />
                    <Text label="Role / function" value={m.role} onChange={(v) => updM(m.id, "role", v)} />
                  </div>
                  <Text label="LinkedIn URL" value={m.linkedin} onChange={(v) => updM(m.id, "linkedin", v)} hint="https://www.linkedin.com/in/..." />
                  <ImageUpload label="Photo" value={m.photo} onChange={(v) => updM(m.id, "photo", v)} hint="Portrait (3:4) works best" />
                  <div className="flex gap-3">
                    <Btn onClick={() => saveMember(m)}><Save size={16} /> Save</Btn>
                    <Btn variant="danger" onClick={() => delMember(m.id)}><Trash2 size={16} /> Delete</Btn>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
